import { GuaranteeType } from "@interfaces/catalogs";
import {
  BofuTabDocument,
  BofuTabStatus,
  DocumentResponse,
  TabsConfigResponseDocument,
  PersonaStructure,
  BofuDocumentRelationship,
  GarantiasVehicularInmobiliariaResponse,
  FORM_STATUS_ENUM,
} from "@interfaces/response";
import { PersonalData } from "@interfaces/store";
import { UUID } from "crypto";

export const validateDocumentStructure = (document: TabsConfigResponseDocument) => {
  if (
    !document.name ||
    !document.label ||
    !document.file_category_id ||
    !document.file_category_code ||
    !document.relationship
  ) {
    return false;
  }

  return true;
};

export const filterDocumentsByGuarantee = (
  document: TabsConfigResponseDocument,
  guarantee_type: GuaranteeType | undefined,
) => {
  if (document.guarantee_type_code && guarantee_type) {
    if (document.guarantee_type_code === guarantee_type.code) {
      return true;
    } else {
      return false;
    }
  }

  return true;
};

export const someDocumentRejected = (documents: BofuTabDocument[]): boolean => {
  // identificar los documentos unicos/multiples
  const unique_documents = documents.filter((item) => !item.multiple);
  const multiple_documents = documents.filter((item) => item.multiple);

  // validar si algun documento esta rechazado
  const result_unique = unique_documents.some((item) => item.file_value?.validated === false);
  if (result_unique) {
    return true;
  }

  const result_multiple = multiple_documents.some((item) => {
    return item?.list_values?.some((item) => item?.validated === false);
  });
  if (result_multiple) {
    return true;
  }

  return false;
};

export const allDocumentValidated = (documents: BofuTabDocument[]): boolean => {
  const unique_documents = documents.filter((item) => !item.multiple);
  const multiple_documents = documents.filter((item) => item.multiple);

  const result_unique = unique_documents.every((item) => item.file_value?.validated === true);
  const result_multiple = multiple_documents.every((item) =>
    item?.list_values?.every((item) => item?.validated === true),
  );

  return result_unique && result_multiple;
};

export const allDocumentUploaded = (documents: BofuTabDocument[]): boolean => {
  const unique_documents = documents.filter((item) => !item.multiple);
  const multiple_documents = documents.filter((item) => item.multiple);

  const result_unique = unique_documents.every((item) => item.file_value);
  const result_multiple = multiple_documents.every((item) => {
    // si se requiere al menos un documento seria con lo siguiente:
    // return item?.list_values?.some((item) => item);
    return item?.list_values?.every((item) => item);
  });

  return result_unique && result_multiple;
};

interface generateTabLabelProps {
  tab_documents: BofuTabDocument[];
  other_documents?: (DocumentResponse | undefined)[];
}

export const generateTabLabel = ({
  tab_documents,
  other_documents,
}: generateTabLabelProps): BofuTabStatus | undefined => {
  // filtrar los documentos requeridos
  const required_documents = tab_documents.filter((item) => item.required);

  // validar si algun documento esta rechazado
  const some_document_rejected = someDocumentRejected(required_documents);
  const some_other_document_rejected = other_documents?.length
    ? other_documents?.some((item) => item?.validated === false)
    : false;
  if (some_document_rejected || some_other_document_rejected) {
    return "incomplete";
  }

  // validar si todos los documentos estan validados
  const all_documents_validated = allDocumentValidated(required_documents);
  const all_other_documents_validated = other_documents?.length
    ? other_documents?.every((item) => item?.validated === true)
    : true;
  if (all_documents_validated && all_other_documents_validated) {
    return "validado";
  }

  // validar si todos los documentos estan cargados
  const all_documents_uploaded = allDocumentUploaded(required_documents);
  const all_other_documents_uploaded = other_documents?.length ? other_documents?.every((item) => item) : true;
  if (all_documents_uploaded && all_other_documents_uploaded) {
    return "cargado";
  }

  return undefined;
};

export const validateRequiredDocumentsAreUploaded = (documents: BofuTabDocument[]) => {
  const required_documents = documents.filter((item) => item.required);
  const documents_uploaded = required_documents.filter((item) => item.file_value !== undefined);
  return documents_uploaded.length === required_documents.length;
};

interface IdentityDocumentsProps {
  documents: DocumentResponse[];
  representante?: PersonaStructure;
  aval?: PersonaStructure;
  conyuge_aval?: PersonaStructure;
  segundo_aval?: PersonaStructure;
  client_details?: PersonalData;
}

export const identityDocumentsByPerson = ({
  documents,
  representante,
  aval,
  conyuge_aval,
  segundo_aval,
  client_details,
}: IdentityDocumentsProps) => {
  const docs_account = client_details ? documents.filter((item) => item.client_details_id === client_details.id) : [];
  const docs_representante = representante ? documents.filter((item) => item.person_id === representante.id) : [];
  const docs_aval = aval ? documents.filter((item) => item.person_id === aval.id) : [];
  const docs_conyuge_aval = conyuge_aval ? documents.filter((item) => item.person_id === conyuge_aval.id) : [];
  const docs_segundo_aval = segundo_aval ? documents.filter((item) => item.person_id === segundo_aval.id) : [];
  const docs_guarantee = documents.filter((item) => item.guarantee_id);

  return {
    docs_account,
    docs_representante,
    docs_aval,
    docs_conyuge_aval,
    docs_segundo_aval,
    docs_guarantee,
  };
};

interface GenerateDocumentConfigProps {
  docItem: TabsConfigResponseDocument;
  docs_account?: DocumentResponse[];
  docs_representante: DocumentResponse[];
  docs_aval: DocumentResponse[];
  docs_conyuge_aval: DocumentResponse[];
  docs_segundo_aval: DocumentResponse[];
  clientDetailsId: UUID;
  representante: PersonaStructure | undefined;
  aval: PersonaStructure | undefined;
  conyuge_aval: PersonaStructure | undefined;
  segundo_aval: PersonaStructure | undefined;
}

export const generateDocumentConfig = ({
  docItem,
  docs_account,
  docs_representante,
  docs_aval,
  docs_conyuge_aval,
  docs_segundo_aval,
  clientDetailsId,
  representante,
  aval,
  conyuge_aval,
  segundo_aval,
}: GenerateDocumentConfigProps): BofuTabDocument => {
  let document_value: DocumentResponse | undefined;
  let list_values: Array<DocumentResponse | undefined> = [];
  let client_details_id: string | undefined;
  let person_id: string | undefined;
  let disabled = true;
  let upload_type: "client" | "person" | undefined;

  switch (docItem.relationship) {
    case "client_details":
      document_value = docs_account?.find((item) => item.file_code === docItem.file_category_code);
      list_values = docs_account?.filter((item) => item.file_code === docItem.file_category_code) || [];
      client_details_id = clientDetailsId;
      person_id = undefined;
      disabled = false;
      upload_type = "client";
      break;
    case "representante_legal":
      document_value = docs_representante.find((item) => item.file_code === docItem.file_category_code);
      list_values = docs_representante.filter((item) => item.file_code === docItem.file_category_code);
      client_details_id = clientDetailsId;
      person_id = representante ? representante.id : undefined;
      disabled = representante ? false : true;
      upload_type = "person";
      break;
    case "aval":
      document_value = docs_aval.find((item) => item.file_code === docItem.file_category_code);
      list_values = docs_aval.filter((item) => item.file_code === docItem.file_category_code);
      client_details_id = clientDetailsId;
      person_id = aval ? aval.id : undefined;
      disabled = aval ? false : true;
      upload_type = "person";
      break;
    case "conyuge_aval":
      document_value = docs_conyuge_aval.find((item) => item.file_code === docItem.file_category_code);
      list_values = docs_conyuge_aval.filter((item) => item.file_code === docItem.file_category_code);
      client_details_id = clientDetailsId;
      person_id = conyuge_aval ? conyuge_aval.id : undefined;
      disabled = conyuge_aval ? false : true;
      upload_type = "person";
      break;
    case "propietario_real":
      // Estos se retornan vacios por que puede haber varios propietarios reales
      document_value = undefined;
      list_values = [];
      client_details_id = undefined;
      person_id = undefined;
      disabled = true;
      upload_type = undefined;
      break;
    case "segundo_aval":
      document_value = docs_segundo_aval.find((item) => item.file_code === docItem.file_category_code);
      list_values = docs_segundo_aval.filter((item) => item.file_code === docItem.file_category_code);
      client_details_id = clientDetailsId;
      person_id = segundo_aval ? segundo_aval.id : undefined;
      disabled = segundo_aval ? false : true;
      upload_type = "person";
      break;
  }

  return {
    name: docItem.name,
    label: docItem.label,
    order: docItem.order,
    required: docItem.required,
    file_category_id: docItem.file_category_id,
    file_category_code: docItem.file_category_code,
    relationship: docItem.relationship as BofuDocumentRelationship,
    file_value: document_value,
    client_details_id: client_details_id as UUID,
    person_id: person_id as UUID,
    guarantee_id: undefined,
    disabled,
    upload_type,
    multiple: docItem.multiple || false,
    list_values: list_values.length ? list_values : [undefined],
  };
};

interface GeneratePropietarioDocsProps {
  propietarioItem: PersonaStructure;
  valid_documents: BofuTabDocument[];
  documents_uploaded: DocumentResponse[];
  clientDetailsId: UUID;
}

export const generatePropietarioDocs = ({
  propietarioItem,
  valid_documents,
  documents_uploaded,
  clientDetailsId,
}: GeneratePropietarioDocsProps): BofuTabDocument[] => {
  const copy_documents = JSON.parse(JSON.stringify(valid_documents)) as BofuTabDocument[];
  copy_documents.forEach((documentItem) => {
    documentItem.client_details_id = clientDetailsId;
    documentItem.person_id = propietarioItem.id as UUID;
    documentItem.disabled = false;
    documentItem.upload_type = "person";
    documentItem.list_values = [];
    documentItem.file_value = undefined;

    if (documentItem.multiple) {
      documentItem.list_values = documents_uploaded
        .filter((item) => item.file_code === documentItem.file_category_code)
        .filter((item) => item.person_id === propietarioItem.id);

      if (documentItem.list_values.length === 0) {
        documentItem.list_values.push(undefined);
      }
    } else {
      documentItem.file_value = documents_uploaded.find(
        (item) => item.file_code === documentItem.file_category_code && item.person_id === propietarioItem.id,
      );
    }
  });

  return copy_documents;
};

interface generateGuaranteeDocsProps {
  guaranteeItem: GarantiasVehicularInmobiliariaResponse;
  valid_documents: BofuTabDocument[];
  documents_uploaded: DocumentResponse[];
  clientDetailsId: UUID;
}

export const generateGuaranteeDocs = ({
  clientDetailsId,
  guaranteeItem,
  valid_documents,
  documents_uploaded,
}: generateGuaranteeDocsProps): BofuTabDocument[] => {
  const copy_documents = JSON.parse(JSON.stringify(valid_documents)) as BofuTabDocument[];

  copy_documents.forEach((documentItem) => {
    documentItem.client_details_id = clientDetailsId;
    documentItem.person_id = undefined;
    documentItem.guarantee_id = guaranteeItem.id;
    documentItem.upload_type = "client";
    documentItem.list_values = [];
    documentItem.file_value = undefined;

    if (documentItem.multiple) {
      documentItem.list_values = documents_uploaded
        .filter((item) => item.file_code === documentItem.file_category_code)
        .filter((item) => item.guarantee_id === guaranteeItem.id);

      if (documentItem.list_values.length === 0) {
        documentItem.list_values.push(undefined);
      }
    } else {
      documentItem.file_value = documents_uploaded.find(
        (item) => item.file_code === documentItem.file_category_code && item.guarantee_id === guaranteeItem.id,
      );
    }
  });

  return copy_documents;
};

interface GenerateGuaranteeTitleProps {
  guarantee_item: GarantiasVehicularInmobiliariaResponse;
  guarantee_index: number;
}

export const generateGuaranteeTitle = ({ guarantee_item, guarantee_index }: GenerateGuaranteeTitleProps) => {
  if (guarantee_item.details) {
    const { make, model, year, version } = guarantee_item.details;
    if (make && model && year && version) {
      return `${make} | ${model} | ${year} | ${version}`;
    }
  }
  return `Garantía ${guarantee_index + 1}`;
};

export const isFormAvailable = (form_status: FORM_STATUS_ENUM) => {
  if (form_status === "COMPLETO" || form_status === "VALIDADO") {
    return false;
  }
  return true;
};
